import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { PROJECTS } from "@/data/projects";

export default function FeaturedProjects() {
  const featured = PROJECTS.slice(0, 6);
  return (
    <section className="relative bg-mist py-24 sm:py-32" data-testid="projects-section">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <SectionHeader
            overline="Selected work"
            title={<>Projects that <span className="text-emerald-brand">move</span> regions forward.</>}
            subtitle="From mobility corridors to luxury resorts and cultural landmarks — each project carries the same engineering discipline."
          />
          <Link
            to="/projects"
            data-testid="projects-section-view-all"
            className="inline-flex items-center gap-2 text-navy font-semibold text-xs tracking-[0.2em] uppercase border-b border-gold pb-1 hover:gap-3 transition-all whitespace-nowrap"
          >
            View portfolio <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {featured.map((p, i) => {
            const span = i === 0 ? "lg:col-span-8" : i === 1 ? "lg:col-span-4" : i === 2 ? "lg:col-span-4" : i === 3 ? "lg:col-span-4" : i === 4 ? "lg:col-span-4" : "lg:col-span-12";
            const aspect = i === 0 ? "aspect-[16/10]" : i === 5 ? "aspect-[21/8]" : "aspect-[4/3]";
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
                className={`group relative overflow-hidden ${span} ${aspect} bg-navy cursor-pointer`}
                data-testid={`project-card-${p.slug}`}
              >
                <img
                  src={p.cover}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent opacity-90" />

                <div className="relative h-full flex flex-col justify-between p-6 sm:p-8 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <span className="overline px-3 py-1 bg-white/10 backdrop-blur border border-white/20">
                      {p.category}
                    </span>
                    <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 text-xs text-white/70">
                      <MapPin size={12} className="text-gold" />
                      {p.location}
                    </div>
                    <h3 className="mt-2 font-display font-bold text-xl sm:text-2xl lg:text-3xl leading-tight">
                      {p.title}
                    </h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.services.slice(0, 3).map((s) => (
                        <span key={s} className="text-[10px] tracking-wider uppercase px-2 py-1 border border-white/20 text-white/80">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
